import React, { useEffect, useState } from 'react';
import AdminSidebar from '../components/admin/AdminSidebar';
import AdminLogin from '../components/admin/AdminLogin';
import BrandingSection from '../components/admin/sections/BrandingSection';
import HeaderSection from '../components/admin/sections/HeaderSection';
import NavigationSection from '../components/admin/sections/NavigationSection';
import SidebarSection from '../components/admin/sections/SidebarSection';
import HeroSection from '../components/admin/sections/HeroSection';
import GameCardsSection from '../components/admin/sections/GameCardsSection';
import PromotionsSection from '../components/admin/sections/PromotionsSection';
import OriginalsSection from '../components/admin/sections/OriginalsSection';
import InPlaySection from '../components/admin/sections/InPlaySection';
import ModalsSection from '../components/admin/sections/ModalsSection';
import FooterSection from '../components/admin/sections/FooterSection';
import DashboardSection from '../components/admin/sections/DashboardSection';
import LeadsSection from '../components/admin/sections/LeadsSection';
import ProductsSection from '../components/admin/sections/ProductsSection';
import MarqueeSection from '../components/admin/sections/MarqueeSection';
import SettingsSection from '../components/admin/sections/SettingsSection';
import BotConfigSection from '../components/admin/sections/BotConfigSection';
import BotSettingSection from '../components/admin/sections/BotSettingSection';
import ExchangeListingsSection from '../components/admin/sections/ExchangeListingsSection';
import DealingTermsSection from '../components/admin/sections/DealingTermsSection';
import PaymentsWeAcceptSection from '../components/admin/sections/PaymentsWeAcceptSection';
import SectionVisibilitySection from '../components/admin/sections/SectionVisibilitySection';
import UsersSection from '../components/admin/sections/UsersSection';
import StaffSection from '../components/admin/sections/StaffSection';
import PaymentMethodsSection from '../components/admin/sections/PaymentMethodsSection';
import AIConfigSection from '../components/admin/sections/AIConfigSection';
import ChatSection from '../components/admin/sections/ChatSection';
import ReviewsSection from '../components/admin/sections/ReviewsSection';
import FlyerAdSection from '../components/admin/sections/FlyerAdSection';
import { Menu, LogOut, ShieldCheck } from 'lucide-react';
import { adminLogin, adminMe, getAdminToken, setAdminToken } from '../lib/api';
import { toast } from 'sonner';
import ForcePasswordChangeModal from '../components/admin/ForcePasswordChangeModal';

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [active, setActive] = useState('dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tempPwd, setTempPwd] = useState(''); // remember password user just typed on login

  useEffect(() => {
    let alive = true;
    if (!getAdminToken()) { setAuthed(false); setChecking(false); return; }
    adminMe()
      .then((u) => { if (!alive) return; setMe(u); setAuthed(true); })
      .catch(() => alive && setAuthed(false))
      .finally(() => alive && setChecking(false));
    return () => { alive = false; };
  }, []);

  const handleLogin = async (u, p) => {
    try {
      await adminLogin(u, p);
      const meData = await adminMe();
      setMe(meData);
      setTempPwd(p);
      setAuthed(true);
      toast.success(`Welcome ${meData.role === 'super_admin' ? 'Super Admin' : 'Admin'}`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid credentials');
      return false;
    }
  };

  const logout = () => {
    setAdminToken(null);
    setMe(null);
    setAuthed(false);
  };

  if (checking) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-400 text-sm">Loading...</div>;
  }

  if (!authed) return <AdminLogin onLogin={handleLogin} />;

  const isSuper = me?.role === 'super_admin';
  const isStaff = me?.role === 'staff';
  const staffPerms = isStaff && Array.isArray(me?.permissions) ? me.permissions : null;

  const allSections = {
    dashboard: <DashboardSection setActive={setActive} />,
    enquiries: <LeadsSection />,
    branding: <BrandingSection />,
    header: <HeaderSection />,
    navigation: <NavigationSection />,
    sidebar: <SidebarSection />,
    services: <SidebarSection />,
    hero: <HeroSection />,
    marquee: <MarqueeSection />,
    gamecards: <GameCardsSection />,
    products: <ProductsSection />,
    exchanges: <ExchangeListingsSection />,
    dealingterms: <DealingTermsSection />,
    payments: <PaymentsWeAcceptSection />,
    visibility: <SectionVisibilitySection />,
    promotions: <PromotionsSection />,
    flyerad: <FlyerAdSection />,
    originals: <OriginalsSection />,
    inplay: <InPlaySection />,
    modals: <ModalsSection />,
    footer: <FooterSection />,
    settings: <SettingsSection />,
    botconfig: <BotConfigSection />,
    botsetting: <BotSettingSection />,
    users: <UsersSection />,
    payment: <PaymentMethodsSection />,
    reviews: <ReviewsSection />,
    aiconfig: <AIConfigSection />,
    chat: <ChatSection />,
    ...(isSuper || (!isStaff) ? { staff: <StaffSection /> } : {})
  };
  // Apply staff permission filter — dashboard is no longer auto-granted to staff;
  // an admin must explicitly tick the "Dashboard" permission. Staff with no granted
  // sections see a "Pending approval" placeholder below.
  const sections = staffPerms
    ? Object.fromEntries(Object.entries(allSections).filter(([k]) => staffPerms.includes(k)))
    : allSections;

  const titles = {
    dashboard: 'Dashboard',
    enquiries: 'Enquiries & Leads',
    branding: 'Branding & Colors',
    header: 'Header Settings',
    navigation: 'Top Navigation',
    sidebar: 'Control Panel',
    services: 'Services Catalog',
    hero: 'Hero Slides',
    marquee: 'Logo Marquee & Social',
    gamecards: 'Service Cards',
    products: 'Products (B2C / B2B)',
    exchanges: 'Exchange Listings',
    dealingterms: 'Dealing Terms',
    payments: 'Payments We Accept',
    visibility: 'Section Visibility',
    promotions: 'Promotions & Social',
    flyerad: 'Flyer Ad Popup',
    originals: 'Platforms Showcase',
    inplay: 'Top Rated Platforms',
    modals: 'Login / Signup Modals',
    footer: 'Footer Content',
    settings: 'Settings',
    botconfig: 'OTP Setup',
    botsetting: 'Bot Setting',
    users: 'Registered Users',
    staff: 'Staff & Permissions',
    payment: 'Payment Methods',
    reviews: 'Reviews & Ratings',
    aiconfig: 'AI Chat Bot',
    chat: 'Live Chat'
  };

  // Guard: if section is not granted, redirect to the first allowed section (or stay
  // for the "pending approval" placeholder).
  const sectionKeys = Object.keys(sections);
  const activeKey = sections[active] ? active : (sectionKeys[0] || 'dashboard');

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex">
      <AdminSidebar
        active={activeKey}
        onChange={(k) => { setActive(k); setMobileOpen(false); }}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        isSuper={isSuper}
        staffPerms={staffPerms}
      />

      <div className="flex-1 min-w-0 lg:ml-72 flex flex-col min-h-screen">
        {/* Topbar — minimalist: hamburger + page title + logout */}
        <div className="sticky top-0 z-20 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 px-4 md:px-6 py-3 flex items-center gap-3 shrink-0">
          <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 -ml-2 text-zinc-300 hover:text-white relative z-[80]" data-testid="admin-mobile-menu-btn" style={{ touchAction: 'manipulation' }}>
            <Menu size={20} />
          </button>
          <h1 className="text-lg md:text-xl font-bold" data-testid="admin-page-title">{titles[activeKey]}</h1>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={logout} className="flex items-center gap-1.5 text-xs md:text-sm px-3 py-2 rounded-md bg-red-600/15 hover:bg-red-600/25 text-red-300 border border-red-900/40" data-testid="admin-logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        <div className="p-4 md:p-6 max-w-[1400px] w-full">
          {staffPerms && staffPerms.length === 0 ? (
            <div className="rounded-xl bg-zinc-900 border border-zinc-800 p-10 text-center" data-testid="staff-pending-approval">
              <div className="w-16 h-16 mx-auto rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mb-4">
                <ShieldCheck size={28} className="text-amber-300" />
              </div>
              <h2 className="text-xl font-bold text-zinc-100 mb-1">Awaiting Admin Approval</h2>
              <p className="text-zinc-400 text-sm">Your account is active but no sections have been granted yet. Ask your admin to enable the dashboard or other permissions.</p>
            </div>
          ) : sections[activeKey] || (
            <div className="text-zinc-500 text-sm">This section is not available with your current permissions.</div>
          )}
        </div>
      </div>

      <ForcePasswordChangeModal
        open={!!me?.must_change_password}
        currentPassword={tempPwd}
        onSuccess={() => { setMe({ ...me, must_change_password: false }); setTempPwd(''); }}
      />
    </div>
  );
}
